import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  MenuSeparator,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  RefreshCw,
  Plus,
  CalendarOff,
  Pencil,
  Play,
  AlertTriangle,
  Trash2,
  MoreVertical,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  CheckCircle,
} from "lucide-react";
import MultiSelect from "../ui/MultiSelect";
import "./ScheduledAnalysis.css";
import { BASEURL, ERROR_MESSAGE_TIMEOUT } from "../../utils/constants";
import { apiRequest, isUserAdmin } from "../../utils/authUtils";
import {
  setSchedules,
  setLoading,
  setError,
  setRefreshing,
  setSelectedSchedule,
  setModalOpen,
  setModalMode,
  setConfirmOpen,
  setAvailableFrameworks,
  setAvailableClients,
  setLoadingClients,
  setClientFrameworkMap,
  setAvailableFilters,
  setLoadingFilters,
  setSaving,
  setFilters,
  clearError,
  removeScheduleFromList,
} from "../../utils/scheduledAnalysisSlice";

/* ── Badge color map ── */
const BADGE_CLASSES = {
  green: "bg-green-500/15 text-green-600 dark:text-green-400",
  blue: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  red: "bg-red-500/15 text-red-600 dark:text-red-400",
  orange: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
  purple: "bg-purple-500/15 text-purple-600 dark:text-purple-400",
  grey: "bg-gray-500/15 text-gray-600 dark:text-gray-400",
  teal: "bg-teal-500/15 text-teal-600 dark:text-teal-400",
};
const getBadgeClasses = (color) => BADGE_CLASSES[color] || BADGE_CLASSES.grey;

/**
 * ScheduledAnalysis Component
 * Admin page for managing scheduled analyses
 */
const ScheduledAnalysis = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAdmin = isUserAdmin();

  const {
    schedules,
    loading,
    error,
    refreshing,
    selectedSchedule,
    modalOpen,
    modalMode,
    confirmOpen,
    availableFrameworks,
    availableClients,
    clientFrameworkMap,
    loadingClients,
    availableFilters,
    loadingFilters,
    saving,
    filters,
  } = useSelector((state) => state.scheduledAnalysis);

  const isMenuOpen = useSelector((state) => state.app.sidebar.pinned);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [scheduleMode, setScheduleMode] = useState("simple");
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [selectedError, setSelectedError] = useState("");
  const itemsPerPage = 10;

  // Form state
  const [formData, setFormData] = useState({
    analysis_name: "",
    description: "",
    client: "",
    dataset_name: [],
    frameworks: [],
    distribution_column: "",
    filter_values: {},
    frequency: "daily",
    time_of_day: "09:00",
    timezone: "UTC",
    cron_expression: "",
    allowed_users: "",
  });

  // Helper to format dataset names: "sales_data" -> "Sales Data"
  const formatDatasetName = (name) =>
    name
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  // Client options derived from API data
  const filterClientOptions = [
    { key: "all", text: "All Clients", value: "all" },
    ...availableClients.map((c) => ({
      key: c.name,
      text: c.name.charAt(0).toUpperCase() + c.name.slice(1),
      value: c.name,
    })),
  ];

  const formClientOptions = availableClients.map((c) => ({
    key: c.name,
    text: c.name.charAt(0).toUpperCase() + c.name.slice(1),
    value: c.name,
  }));

  // Dataset options derived from selected client
  const formDatasetOptions = (
    availableClients.find((c) => c.name === formData.client)?.datasets || []
  ).map((d) => ({
    key: d,
    text: formatDatasetName(d),
    value: d,
  }));

  const statusOptions = [
    { key: "all", text: "All Status", value: "all" },
    { key: "active", text: "Active", value: "active" },
    { key: "processing", text: "Processing", value: "processing" },
    { key: "paused", text: "Paused", value: "paused" },
    { key: "failed", text: "Failed", value: "failed" },
  ];

  const frequencyOptions = [
    { key: "hourly", text: "Hourly", value: "hourly" },
    { key: "daily", text: "Daily", value: "daily" },
    { key: "weekly", text: "Weekly", value: "weekly" },
    { key: "monthly", text: "Monthly", value: "monthly" },
  ];

  const timezoneOptions = [
    { key: "UTC", text: "UTC", value: "UTC" },
    { key: "America/New_York", text: "America/New_York", value: "America/New_York" },
    { key: "America/Los_Angeles", text: "America/Los_Angeles", value: "America/Los_Angeles" },
    { key: "Europe/London", text: "Europe/London", value: "Europe/London" },
    { key: "Asia/Kolkata", text: "Asia/Kolkata", value: "Asia/Kolkata" },
  ];

  // SECURITY: Redirect non-admin users
  useEffect(() => {
    if (!isAdmin) {
      navigate("/beagle-chat", { replace: true });
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchSchedules();
    fetchClients();
  }, [isAdmin]);

  // Reactive trigger for server-side filters
  useEffect(() => {
    if (isAdmin) {
      fetchSchedules();
    }
  }, [filters.client, filters.status, isAdmin]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.client, filters.status, filters.searchQuery]);

  useEffect(() => {
    if (errorMessage) {
      const timeout = setTimeout(() => {
        setErrorMessage("");
        dispatch(clearError());
      }, ERROR_MESSAGE_TIMEOUT);
      return () => clearTimeout(timeout);
    }
  }, [errorMessage, dispatch]);

  useEffect(() => {
    if (successMessage) {
      const timeout = setTimeout(() => {
        setSuccessMessage("");
      }, ERROR_MESSAGE_TIMEOUT);
      return () => clearTimeout(timeout);
    }
  }, [successMessage]);

  // Populate frameworks when clientFrameworkMap updates
  useEffect(() => {
    if (
      modalOpen &&
      formData.client &&
      formData.dataset_name &&
      clientFrameworkMap[formData.client]
    ) {
      const clientData = clientFrameworkMap[formData.client];
      let allFrameworks = [];
      const datasets = Array.isArray(formData.dataset_name)
        ? formData.dataset_name
        : [formData.dataset_name];

      datasets.forEach((ds) => {
        const frameworks = clientData[ds] || [];
        allFrameworks = [...new Set([...allFrameworks, ...frameworks])];
      });

      dispatch(setAvailableFrameworks(allFrameworks));
    }
  }, [clientFrameworkMap, formData.client, formData.dataset_name, modalOpen, dispatch]);

  // Don't render anything for non-admin users
  if (!isAdmin) {
    return null;
  }

  /* ── API Calls ── */

  const fetchSchedules = async () => {
    dispatch(setLoading(true));
    try {
      let url = `${BASEURL}/api/conversations/scheduled-analysis`;
      const params = new URLSearchParams();
      if (filters.client && filters.client !== "all") {
        params.append("client", filters.client);
      }
      if (filters.status && filters.status !== "all") {
        if (filters.status === "active") {
          params.append("is_active", "true");
        } else if (filters.status === "paused") {
          params.append("is_active", "false");
        } else if (filters.status === "failed") {
          params.append("status", "failed");
        }
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      const response = await apiRequest(url);
      if (!response.ok) throw new Error("Failed to fetch schedules");
      const result = await response.json();
      dispatch(setSchedules(Array.isArray(result) ? result : []));
    } catch (err) {
      console.error("Error fetching schedules:", err);
      dispatch(setError(err.message));
      dispatch(setLoading(false));
      setErrorMessage(err.message);
    }
  };

  const fetchClients = async () => {
    dispatch(setLoadingClients(true));
    try {
      const response = await apiRequest(
        `${BASEURL}/api/conversations/scheduled-analysis/available-clients`
      );
      if (!response.ok) throw new Error("Failed to fetch clients");
      const result = await response.json();
      dispatch(setAvailableClients(result.clients || []));
    } catch (err) {
      console.error("Error fetching clients:", err);
      dispatch(setLoadingClients(false));
    }
  };

  const fetchAvailableFilters = async (client, datasetName) => {
    dispatch(setLoadingFilters(true));
    try {
      const response = await apiRequest(
        `${BASEURL}/api/conversations/scheduled-analysis/available-filters?client=${encodeURIComponent(client)}&dataset_name=${encodeURIComponent(datasetName)}`
      );
      if (!response.ok) throw new Error("Failed to fetch filters");
      const result = await response.json();
      dispatch(setAvailableFilters(result.filter_groups || []));
    } catch (err) {
      console.error("Error fetching filters:", err);
      dispatch(setLoadingFilters(false));
    }
  };

  const fetchClientFrameworks = async (clientName) => {
    if (clientFrameworkMap[clientName]) return;
    try {
      const response = await apiRequest(
        `${BASEURL}/api/conversations/scheduled-analysis/available-frameworks?client=${encodeURIComponent(clientName)}`
      );
      if (!response.ok) throw new Error("Failed to fetch frameworks for client");
      const result = await response.json();
      dispatch(setClientFrameworkMap(result));
    } catch (err) {
      console.error("Error fetching client frameworks:", err);
    }
  };

  /* ── Handlers ── */

  const handleRefresh = async () => {
    dispatch(setRefreshing(true));
    await fetchSchedules();
    dispatch(setRefreshing(false));
  };

  const handleFilterChange = (filterName, value) => {
    dispatch(setFilters({ [filterName]: value }));
  };

  const openModal = (mode, schedule = null) => {
    dispatch(setModalMode(mode));
    dispatch(setSelectedSchedule(schedule));

    if (mode === "edit" && schedule) {
      // Normalize dataset_name to array (M3 fix)
      const dsName = Array.isArray(schedule.dataset_name)
        ? schedule.dataset_name
        : schedule.dataset_name
          ? [schedule.dataset_name]
          : [];

      setFormData({
        analysis_name: schedule.analysis_name || "",
        description: schedule.description || "",
        client: schedule.client || "",
        dataset_name: dsName,
        frameworks: schedule.frameworks || [],
        distribution_column: schedule.distribution_column || "",
        filter_values: schedule.filter_values || {},
        frequency: schedule.frequency || "daily",
        time_of_day: schedule.time_of_day || "09:00",
        timezone: schedule.timezone || "UTC",
        cron_expression: schedule.cron_expression || "",
        allowed_users: schedule.allowed_users?.join(", ") || "",
      });
      if (schedule.client) fetchClientFrameworks(schedule.client);
      if (schedule.client && schedule.dataset_name) {
        const firstDs = Array.isArray(schedule.dataset_name)
          ? schedule.dataset_name[0]
          : schedule.dataset_name;
        if (firstDs) fetchAvailableFilters(schedule.client, firstDs);
      }
      setScheduleMode(
        schedule.frequency ? "simple" : schedule.cron_expression ? "advanced" : "simple"
      );
    } else {
      setFormData({
        analysis_name: "",
        description: "",
        client: "",
        dataset_name: [],
        frameworks: [],
        distribution_column: "",
        filter_values: {},
        frequency: "daily",
        time_of_day: "09:00",
        timezone: "UTC",
        cron_expression: "",
        allowed_users: "",
      });
      dispatch(setAvailableFrameworks([]));
      dispatch(setAvailableFilters([]));
      setScheduleMode("simple");
    }

    dispatch(setModalOpen(true));
  };

  const closeModal = () => {
    dispatch(setModalOpen(false));
    dispatch(setSelectedSchedule(null));
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "client") {
        updated.dataset_name = [];
        updated.frameworks = [];
        updated.filter_values = {};
      }
      if (field === "dataset_name") {
        updated.frameworks = [];
        updated.filter_values = {};
      }
      return updated;
    });

    // Side effects outside updater to avoid double-execution in StrictMode
    if (field === "client") {
      dispatch(setAvailableFrameworks([]));
      dispatch(setAvailableFilters([]));
      if (value) fetchClientFrameworks(value);
    }

    if (field === "dataset_name") {
      let allFrameworks = [];
      const clientData = clientFrameworkMap[formData.client] || {};
      if (Array.isArray(value)) {
        value.forEach((ds) => {
          const frameworks = clientData[ds] || [];
          allFrameworks = [...new Set([...allFrameworks, ...frameworks])];
        });
      } else if (value) {
        allFrameworks = clientData[value] || [];
      }
      dispatch(setAvailableFrameworks(allFrameworks));
      if (Array.isArray(value) && value.length > 0 && formData.client) {
        fetchAvailableFilters(formData.client, value[0]);
      } else {
        dispatch(setAvailableFilters([]));
      }
    }
  };

  const handleFilterValueChange = (colName, value) => {
    setFormData((prev) => ({
      ...prev,
      filter_values: { ...prev.filter_values, [colName]: value },
    }));
  };

  const handleSave = async () => {
    dispatch(setSaving(true));
    try {
      const payload = { ...formData };

      // Clean filter_values: remove keys with empty arrays
      const cleanedFilters = {};
      Object.entries(payload.filter_values || {}).forEach(([k, v]) => {
        if (Array.isArray(v) && v.length > 0) cleanedFilters[k] = v;
      });
      payload.filter_values =
        Object.keys(cleanedFilters).length > 0 ? cleanedFilters : {};

      if (scheduleMode === "simple") {
        payload.cron_expression = "";
      } else {
        payload.frequency = "";
        payload.time_of_day = "";
      }

      payload.allowed_users = formData.allowed_users
        ? formData.allowed_users
            .split(",")
            .map((e) => e.trim())
            .filter((e) => e)
        : null;

      let response;
      if (modalMode === "edit" && selectedSchedule) {
        response = await apiRequest(
          `${BASEURL}/api/conversations/scheduled-analysis/${selectedSchedule.analysis_id}`,
          { method: "PUT", body: JSON.stringify(payload) }
        );
      } else {
        response = await apiRequest(
          `${BASEURL}/api/conversations/scheduled-analysis`,
          { method: "POST", body: JSON.stringify(payload) }
        );
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save schedule");
      }

      await response.json();
      setSuccessMessage(
        modalMode === "edit"
          ? "Schedule updated successfully"
          : "Schedule created successfully"
      );
      closeModal();
      fetchSchedules();
    } catch (err) {
      console.error("Error saving schedule:", err);
      setErrorMessage(err.message);
    } finally {
      dispatch(setSaving(false));
    }
  };

  const confirmDelete = (schedule) => {
    dispatch(setSelectedSchedule(schedule));
    dispatch(setConfirmOpen(true));
  };

  const handleDelete = async () => {
    if (!selectedSchedule) return;
    try {
      const response = await apiRequest(
        `${BASEURL}/api/conversations/scheduled-analysis/${selectedSchedule.analysis_id}`,
        { method: "DELETE" }
      );
      if (!response.ok) throw new Error("Failed to delete schedule");
      dispatch(removeScheduleFromList(selectedSchedule.analysis_id));
      setSuccessMessage("Schedule deleted successfully");
    } catch (err) {
      console.error("Error deleting schedule:", err);
      setErrorMessage(err.message);
    } finally {
      dispatch(setConfirmOpen(false));
      dispatch(setSelectedSchedule(null));
    }
  };

  const handleRunNow = async (schedule) => {
    try {
      const response = await apiRequest(
        `${BASEURL}/api/conversations/scheduled-analysis/${schedule.analysis_id}/run`,
        { method: "POST" }
      );
      if (!response.ok) throw new Error("Failed to trigger analysis");
      setSuccessMessage("Analysis triggered successfully");
      fetchSchedules();
    } catch (err) {
      console.error("Error triggering analysis:", err);
      setErrorMessage(err.message);
    }
  };

  const handleShowError = (schedule) => {
    setSelectedError(schedule.last_error_message || "No error details available");
    setErrorModalOpen(true);
  };

  /* ── Formatting helpers ── */

  const formatTimeAgo = (time) => {
    if (!time) return "--";
    const date = new Date(time);
    if (isNaN(date.getTime())) return "--";
    const now = new Date();
    const isFuture = date > now;
    const diffMs = Math.abs(now - date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    let label;
    if (diffMins < 60) label = `${diffMins}m`;
    else if (diffHours < 24) label = `${diffHours}h`;
    else label = `${diffDays}d`;
    return isFuture ? `in ${label}` : `${label} ago`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "completed":
        return "green";
      case "processing":
      case "running":
        return "blue";
      case "paused":
        return "orange";
      case "failed":
        return "red";
      default:
        return "grey";
    }
  };

  const getScheduleStatus = (s) => {
    if (s.execution_status === "processing" || s.execution_status === "running")
      return "processing";
    if (s.execution_status === "failed") return "failed";
    if (s.execution_status === "completed") return "active";
    return (s.status || (s.is_active ? "active" : "paused")).toLowerCase();
  };

  const getFrequencyColor = (frequency) => {
    switch (frequency?.toLowerCase()) {
      case "hourly":
        return "blue";
      case "daily":
        return "purple";
      case "weekly":
        return "green";
      case "monthly":
        return "orange";
      default:
        return "teal";
    }
  };

  const formatFrequency = (schedule) => {
    if (schedule.frequency && schedule.frequency !== "") {
      return schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1);
    }
    if (schedule.cron_expression) {
      if (schedule.cron_expression.startsWith("*/")) {
        const mins = schedule.cron_expression.split(" ")[0].replace("*/", "");
        return `Every ${mins}m`;
      }
      return `Cron: ${schedule.cron_expression}`;
    }
    return "--";
  };

  /* ── Filter + Paginate ── */

  const filteredSchedules = schedules.filter((s) => {
    if (filters.client && filters.client !== "all" && s.client !== filters.client)
      return false;
    if (filters.status && filters.status !== "all") {
      if (getScheduleStatus(s) !== filters.status.toLowerCase()) return false;
    }
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const datasetMatch = Array.isArray(s.dataset_name)
        ? s.dataset_name.some(
            (name) => typeof name === "string" && name.toLowerCase().includes(query)
          )
        : typeof s.dataset_name === "string" &&
          s.dataset_name.toLowerCase().includes(query);
      const searchMatch =
        s.analysis_name?.toLowerCase().includes(query) ||
        datasetMatch ||
        s.description?.toLowerCase().includes(query);
      if (!searchMatch) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filteredSchedules.length / itemsPerPage);
  const paginatedSchedules = filteredSchedules.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const frameworkOptions = availableFrameworks.map((f) => ({
    key: f,
    text: f,
    value: f,
  }));

  /* ── Helpers for Listbox display text ── */
  const getOptionText = (options, val) =>
    options.find((o) => o.value === val)?.text || val || "";

  /* ── Render ── */
  return (
    <div
      className={`scheduled-analysis ${!isMenuOpen ? "menu-closed" : ""} w-full min-h-full p-6 max-sm:p-4 transition-[margin-left] duration-300`}
      style={{ background: "var(--bg-primary)" }}
    >
      {/* Page Header */}
      <div className="schedule-header flex justify-between items-start mb-5 flex-wrap gap-4">
        <div>
          <h1
            className="text-[28px] font-semibold m-0 mb-1"
            style={{ color: "var(--text-primary)" }}
          >
            Analysis Scheduling
          </h1>
          <p className="text-sm m-0" style={{ color: "var(--text-secondary)" }}>
            Manage automated analysis workflows
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div
        className="filters-bar flex items-center gap-3 p-3 px-4 rounded-[10px] mb-5 flex-wrap"
        style={{
          background: "var(--card-bg)",
          boxShadow: "0 1px 3px var(--shadow-color)",
        }}
      >
        {/* Client filter */}
        <Listbox
          value={filters.client}
          onChange={(v) => handleFilterChange("client", v)}
        >
          <div className="relative min-w-[160px] max-sm:w-full">
            <ListboxButton
              className="flex items-center justify-between w-full h-[38px] max-sm:h-[34px] px-3 rounded-lg border text-sm transition-colors"
              style={{
                background: "var(--input-bg)",
                borderColor: "var(--border-color)",
                color: "var(--text-primary)",
              }}
            >
              <span className="truncate">
                {getOptionText(filterClientOptions, filters.client) || "Client"}
              </span>
              {loadingClients ? (
                <Loader2
                  size={14}
                  className="animate-spin shrink-0"
                  style={{ color: "var(--text-secondary)" }}
                />
              ) : (
                <ChevronDown
                  size={14}
                  className="shrink-0"
                  style={{ color: "var(--text-secondary)" }}
                />
              )}
            </ListboxButton>
            <ListboxOptions
              anchor="bottom start"
              className="rounded-lg border shadow-lg p-1 [--anchor-gap:4px] max-h-60 overflow-y-auto z-20"
              style={{
                background: "var(--card-bg)",
                borderColor: "var(--border-color)",
              }}
            >
              {filterClientOptions.map((opt) => (
                <ListboxOption
                  key={opt.key}
                  value={opt.value}
                  className="group flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors data-[focus]:bg-[var(--bg-secondary)]"
                  style={{ color: "var(--text-primary)" }}
                >
                  <Check
                    size={14}
                    className="shrink-0 opacity-0 group-data-[selected]:opacity-100"
                    style={{ color: "var(--brand-primary)" }}
                  />
                  {opt.text}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        {/* Status filter */}
        <Listbox
          value={filters.status}
          onChange={(v) => handleFilterChange("status", v)}
        >
          <div className="relative min-w-[160px] max-sm:w-full">
            <ListboxButton
              className="flex items-center justify-between w-full h-[38px] max-sm:h-[34px] px-3 rounded-lg border text-sm transition-colors"
              style={{
                background: "var(--input-bg)",
                borderColor: "var(--border-color)",
                color: "var(--text-primary)",
              }}
            >
              <span className="truncate">
                {getOptionText(statusOptions, filters.status) || "Status"}
              </span>
              <ChevronDown
                size={14}
                className="shrink-0"
                style={{ color: "var(--text-secondary)" }}
              />
            </ListboxButton>
            <ListboxOptions
              anchor="bottom start"
              className="rounded-lg border shadow-lg p-1 [--anchor-gap:4px] max-h-60 overflow-y-auto z-20"
              style={{
                background: "var(--card-bg)",
                borderColor: "var(--border-color)",
              }}
            >
              {statusOptions.map((opt) => (
                <ListboxOption
                  key={opt.key}
                  value={opt.value}
                  className="group flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors data-[focus]:bg-[var(--bg-secondary)]"
                  style={{ color: "var(--text-primary)" }}
                >
                  <Check
                    size={14}
                    className="shrink-0 opacity-0 group-data-[selected]:opacity-100"
                    style={{ color: "var(--brand-primary)" }}
                  />
                  {opt.text}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        {/* Search input */}
        <div className="flex-1 min-w-[200px] max-w-[400px] max-sm:max-w-none max-sm:w-full">
          <input
            type="text"
            placeholder="Search..."
            value={filters.searchQuery}
            onChange={(e) => handleFilterChange("searchQuery", e.target.value)}
            className="w-full h-[38px] max-sm:h-[34px] px-3.5 rounded-lg border text-sm outline-none transition-colors focus:ring-2 focus:ring-brand-500/20"
            style={{
              background: "var(--input-bg)",
              color: "var(--text-primary)",
              borderColor: "var(--border-color)",
            }}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-auto max-sm:w-full">
          <button
            onClick={handleRefresh}
            disabled={loading}
            aria-label="Refresh"
            className="h-[38px] w-[38px] max-sm:h-[34px] max-sm:w-[34px] flex items-center justify-center rounded-lg border transition-all hover:scale-[1.02] active:scale-[0.97] disabled:opacity-50 shrink-0"
            style={{
              background: "var(--input-bg)",
              borderColor: "var(--border-color)",
              color: "var(--text-secondary)",
            }}
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          </button>
          <button
            onClick={() => openModal("create")}
            className="h-[38px] max-sm:h-[34px] flex items-center gap-1.5 px-4 max-sm:px-3 rounded-lg font-semibold text-[13px] text-white transition-all hover:-translate-y-px active:translate-y-0 max-sm:flex-1"
            style={{
              background: "var(--brand-primary)",
              color: "white",
              boxShadow: "0 1px 3px rgba(1, 165, 166, 0.3)",
            }}
          >
            <Plus size={16} /> Create Schedule
          </button>
        </div>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div
          className="flex items-start gap-3 p-4 rounded-lg mb-4 border"
          style={{
            background: "var(--error-bg)",
            borderColor: "var(--error-border)",
          }}
        >
          <AlertTriangle size={18} className="text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-sm text-red-600 dark:text-red-400 m-0">
              Error
            </p>
            <p className="text-sm m-0 mt-1" style={{ color: "var(--text-primary)" }}>
              {errorMessage}
            </p>
          </div>
          <button
            onClick={() => setErrorMessage("")}
            aria-label="Dismiss"
            className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            style={{ color: "var(--text-secondary)" }}
          >
            <X size={16} />
          </button>
        </div>
      )}
      {successMessage && (
        <div
          className="flex items-start gap-3 p-4 rounded-lg mb-4 border border-green-200 dark:border-green-800"
          style={{ background: "rgba(34,197,94,0.08)" }}
        >
          <CheckCircle size={18} className="text-green-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-sm text-green-700 dark:text-green-400 m-0">
              Success
            </p>
            <p className="text-sm m-0 mt-1" style={{ color: "var(--text-primary)" }}>
              {successMessage}
            </p>
          </div>
          <button
            onClick={() => setSuccessMessage("")}
            aria-label="Dismiss"
            className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            style={{ color: "var(--text-secondary)" }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div
        className="schedule-content rounded-lg p-6 min-h-[400px]"
        style={{
          background: "var(--card-bg)",
          boxShadow: "0 2px 4px var(--shadow-color)",
        }}
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2
              size={40}
              className="animate-spin mb-4"
              style={{ color: "var(--brand-primary)" }}
            />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Loading schedules...
            </p>
          </div>
        ) : paginatedSchedules.length > 0 ? (
          <>
            <div className="overflow-x-auto max-md:overflow-visible">
              <table
                className="sa-table w-full border-collapse rounded-lg overflow-hidden border text-sm"
                style={{
                  borderColor: "var(--border-color)",
                  background: "var(--card-bg)",
                }}
              >
                <thead>
                  <tr>
                    {[
                      "Name",
                      "Client",
                      "Dataset",
                      "Frequency",
                      "Status",
                      "Last Run",
                      "Next Run",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className={`px-4 py-3.5 font-semibold text-[13px] uppercase tracking-wide border-b-2 ${h === "Actions" ? "text-center" : "text-left"}`}
                        style={{
                          background: "var(--bg-secondary)",
                          color: "var(--text-primary)",
                          borderColor: "var(--border-color)",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedSchedules.map((schedule) => (
                    <tr
                      key={schedule.analysis_id}
                      className="transition-colors hover:bg-[var(--bg-secondary)]"
                    >
                      <td
                        data-label="Name"
                        className="px-4 py-3.5 text-sm border-t"
                        style={{
                          color: "var(--text-primary)",
                          borderColor: "var(--border-color)",
                        }}
                      >
                        <div className="name-cell flex flex-col gap-1">
                          <strong className="font-semibold" style={{ color: "var(--text-primary)" }}>
                            {schedule.analysis_name}
                          </strong>
                          {schedule.description && (
                            <span
                              className="text-xs line-clamp-1"
                              style={{ color: "var(--text-secondary)" }}
                            >
                              {schedule.description}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        data-label="Client"
                        className="px-4 py-3.5 text-sm border-t"
                        style={{ borderColor: "var(--border-color)" }}
                      >
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${getBadgeClasses("blue")}`}
                        >
                          {schedule.client}
                        </span>
                      </td>
                      <td
                        data-label="Dataset"
                        className="px-4 py-3.5 text-sm border-t"
                        style={{ borderColor: "var(--border-color)" }}
                      >
                        <code
                          className="text-[13px] px-2 py-0.5 rounded"
                          style={{
                            background: "var(--bg-tertiary)",
                            color: "var(--text-secondary)",
                          }}
                        >
                          {Array.isArray(schedule.dataset_name)
                            ? schedule.dataset_name.join(", ")
                            : schedule.dataset_name}
                        </code>
                      </td>
                      <td
                        data-label="Frequency"
                        className="px-4 py-3.5 text-sm border-t"
                        style={{ borderColor: "var(--border-color)" }}
                      >
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${getBadgeClasses(getFrequencyColor(schedule.frequency))}`}
                        >
                          {formatFrequency(schedule)}
                        </span>
                      </td>
                      <td
                        data-label="Status"
                        className="px-4 py-3.5 text-sm border-t"
                        style={{ borderColor: "var(--border-color)" }}
                      >
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${getBadgeClasses(
                            getStatusColor(
                              schedule.execution_status ||
                                schedule.status ||
                                (schedule.is_active ? "active" : "paused")
                            )
                          )}`}
                        >
                          {(() => {
                            const status = getScheduleStatus(schedule);
                            return status.charAt(0).toUpperCase() + status.slice(1);
                          })()}
                        </span>
                      </td>
                      <td
                        data-label="Last Run"
                        className="px-4 py-3.5 text-sm border-t"
                        style={{
                          color: "var(--text-primary)",
                          borderColor: "var(--border-color)",
                        }}
                      >
                        {formatTimeAgo(schedule.last_run_at)}
                      </td>
                      <td
                        data-label="Next Run"
                        className="px-4 py-3.5 text-sm border-t"
                        style={{
                          color: "var(--text-primary)",
                          borderColor: "var(--border-color)",
                        }}
                      >
                        {schedule.is_active
                          ? formatTimeAgo(schedule.next_run_at)
                          : "--"}
                      </td>
                      <td
                        data-label="Actions"
                        className="px-4 py-3.5 text-sm border-t text-center"
                        style={{ borderColor: "var(--border-color)" }}
                      >
                        <Menu>
                          <MenuButton
                            aria-label="Actions"
                            className="p-1.5 rounded transition-colors hover:bg-[var(--bg-tertiary)]"
                          >
                            <MoreVertical
                              size={16}
                              style={{ color: "var(--text-secondary)" }}
                            />
                          </MenuButton>
                          <MenuItems
                            anchor="bottom end"
                            className="rounded-lg border shadow-lg min-w-[160px] p-1 [--anchor-gap:4px] z-20"
                            style={{
                              background: "var(--card-bg)",
                              borderColor: "var(--border-color)",
                            }}
                          >
                            <MenuItem>
                              <button
                                className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm transition-colors data-[focus]:bg-[var(--bg-secondary)]"
                                style={{ color: "var(--text-primary)" }}
                                onClick={() => openModal("edit", schedule)}
                              >
                                <Pencil size={14} /> Edit
                              </button>
                            </MenuItem>
                            <MenuItem>
                              <button
                                className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm transition-colors data-[focus]:bg-[var(--bg-secondary)]"
                                style={{ color: "var(--text-primary)" }}
                                onClick={() => handleRunNow(schedule)}
                              >
                                <Play size={14} /> Run Now
                              </button>
                            </MenuItem>
                            {schedule.execution_status === "failed" &&
                              schedule.last_error_message && (
                                <MenuItem>
                                  <button
                                    className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm transition-colors data-[focus]:bg-[var(--bg-secondary)]"
                                    style={{ color: "var(--text-primary)" }}
                                    onClick={() => handleShowError(schedule)}
                                  >
                                    <AlertTriangle size={14} /> Show Error
                                  </button>
                                </MenuItem>
                              )}
                            <MenuSeparator
                              className="my-1 border-t"
                              style={{ borderColor: "var(--border-color)" }}
                            />
                            <MenuItem>
                              <button
                                className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-red-500 transition-colors data-[focus]:bg-red-500/10"
                                onClick={() => confirmDelete(schedule)}
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                            </MenuItem>
                          </MenuItems>
                        </Menu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="sa-pagination flex justify-center items-center gap-1 mt-5">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border text-sm transition-colors disabled:opacity-40"
                  style={{
                    background: "var(--card-bg)",
                    borderColor: "var(--border-color)",
                    color: "var(--text-secondary)",
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                        page === currentPage ? "font-semibold" : "border"
                      }`}
                      style={
                        page === currentPage
                          ? {
                              background: "var(--brand-primary)",
                              color: "#fff",
                            }
                          : {
                              background: "var(--card-bg)",
                              borderColor: "var(--border-color)",
                              color: "var(--text-secondary)",
                            }
                      }
                    >
                      {page}
                    </button>
                  )
                )}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border text-sm transition-colors disabled:opacity-40"
                  style={{
                    background: "var(--card-bg)",
                    borderColor: "var(--border-color)",
                    color: "var(--text-secondary)",
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <div className="text-center py-16 px-5">
            <CalendarOff
              size={64}
              className="mx-auto mb-5"
              style={{ color: "var(--text-tertiary)" }}
            />
            <h3
              className="text-xl font-semibold mb-2"
              style={{ color: "var(--text-primary)" }}
            >
              No Scheduled Analyses
            </h3>
            <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
              Create your first automated analysis schedule to get started.
            </p>
            <button
              onClick={() => openModal("create")}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg font-semibold text-sm text-white transition-all hover:-translate-y-px"
              style={{ background: "var(--brand-primary)", color: "white" }}
            >
              <Plus size={16} /> Create Schedule
            </button>
          </div>
        )}
      </div>

      {/* ── Create/Edit Modal ── */}
      <Dialog open={modalOpen} onClose={closeModal} className="relative z-50">
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            className="w-full max-w-3xl max-h-[85vh] flex flex-col rounded-xl shadow-2xl"
            style={{ background: "var(--card-bg)" }}
          >
            <DialogTitle
              className="px-6 py-5 border-b font-semibold text-xl shrink-0"
              style={{
                borderColor: "var(--border-color)",
                color: "var(--text-primary)",
              }}
            >
              {modalMode === "create" ? "Create Schedule" : "Edit Schedule"}
            </DialogTitle>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Section 1: Basic Information */}
              <div
                className="mb-6 pb-5 border-b"
                style={{ borderColor: "var(--border-color)" }}
              >
                <span
                  className="block text-[11px] font-bold uppercase tracking-wider mb-4"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Basic Information
                </span>

                <div className="mb-4">
                  <label
                    className="block text-sm font-semibold mb-2"
                    style={{ color: "var(--text-primary)" }}
                  >
                    Analysis Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Weekly Sales Performance Report"
                    value={formData.analysis_name}
                    onChange={(e) =>
                      handleFormChange("analysis_name", e.target.value)
                    }
                    className="w-full px-3 py-2.5 rounded-md border text-sm outline-none transition-all focus:ring-2 focus:ring-brand-500/20"
                    style={{
                      background: "var(--input-bg)",
                      color: "var(--text-primary)",
                      borderColor: "var(--border-color)",
                    }}
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-semibold mb-2"
                    style={{ color: "var(--text-primary)" }}
                  >
                    Description
                  </label>
                  <textarea
                    placeholder="Describe the purpose of this analysis..."
                    value={formData.description}
                    onChange={(e) =>
                      handleFormChange("description", e.target.value)
                    }
                    rows={3}
                    className="w-full px-3 py-2.5 rounded-md border text-sm outline-none transition-all focus:ring-2 focus:ring-brand-500/20 resize-none"
                    style={{
                      background: "var(--input-bg)",
                      color: "var(--text-primary)",
                      borderColor: "var(--border-color)",
                    }}
                  />
                </div>
              </div>

              {/* Section 2: Data Configuration */}
              <div
                className="mb-6 pb-5 border-b"
                style={{ borderColor: "var(--border-color)" }}
              >
                <span
                  className="block text-[11px] font-bold uppercase tracking-wider mb-4"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Data Configuration
                </span>

                <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1 mb-4">
                  {/* Client */}
                  <div>
                    <label
                      className="block text-sm font-semibold mb-2"
                      style={{ color: "var(--text-primary)" }}
                    >
                      Client <span className="text-red-500">*</span>
                    </label>
                    <Listbox
                      value={formData.client}
                      onChange={(v) => handleFormChange("client", v)}
                    >
                      <div className="relative">
                        <ListboxButton
                          className="flex items-center justify-between w-full px-3 py-2.5 rounded-md border text-sm"
                          style={{
                            background: "var(--input-bg)",
                            borderColor: "var(--border-color)",
                            color: formData.client
                              ? "var(--text-primary)"
                              : "var(--text-secondary)",
                          }}
                        >
                          <span className="truncate">
                            {getOptionText(formClientOptions, formData.client) ||
                              "Select client"}
                          </span>
                          {loadingClients ? (
                            <Loader2
                              size={14}
                              className="animate-spin shrink-0"
                              style={{ color: "var(--text-secondary)" }}
                            />
                          ) : (
                            <ChevronDown
                              size={14}
                              className="shrink-0"
                              style={{ color: "var(--text-secondary)" }}
                            />
                          )}
                        </ListboxButton>
                        <ListboxOptions
                          anchor="bottom start"
                          className="rounded-lg border shadow-lg p-1 [--anchor-gap:4px] max-h-60 overflow-y-auto z-[60]"
                          style={{
                            background: "var(--card-bg)",
                            borderColor: "var(--border-color)",
                          }}
                        >
                          {formClientOptions.map((opt) => (
                            <ListboxOption
                              key={opt.key}
                              value={opt.value}
                              className="group flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors data-[focus]:bg-[var(--bg-secondary)]"
                              style={{ color: "var(--text-primary)" }}
                            >
                              <Check
                                size={14}
                                className="shrink-0 opacity-0 group-data-[selected]:opacity-100"
                                style={{ color: "var(--brand-primary)" }}
                              />
                              {opt.text}
                            </ListboxOption>
                          ))}
                        </ListboxOptions>
                      </div>
                    </Listbox>
                  </div>

                  {/* Dataset (multi-select) */}
                  <div>
                    <label
                      className="block text-sm font-semibold mb-2"
                      style={{ color: "var(--text-primary)" }}
                    >
                      Dataset <span className="text-red-500">*</span>
                    </label>
                    <MultiSelect
                      options={formDatasetOptions}
                      value={
                        Array.isArray(formData.dataset_name)
                          ? formData.dataset_name
                          : formData.dataset_name
                            ? [formData.dataset_name]
                            : []
                      }
                      onChange={(v) => handleFormChange("dataset_name", v)}
                      placeholder={
                        formData.client
                          ? "Select dataset(s)"
                          : "Select a client first"
                      }
                      disabled={!formData.client}
                    />
                  </div>
                </div>

                {/* Frameworks */}
                <div className="mb-4">
                  <label
                    className="block text-sm font-semibold mb-2"
                    style={{ color: "var(--text-primary)" }}
                  >
                    Frameworks <span className="text-red-500">*</span>
                  </label>
                  <MultiSelect
                    options={frameworkOptions}
                    value={formData.frameworks}
                    onChange={(v) => handleFormChange("frameworks", v)}
                    placeholder="Select frameworks"
                    disabled={
                      !formData.dataset_name ||
                      (Array.isArray(formData.dataset_name) &&
                        formData.dataset_name.length === 0)
                    }
                  />
                </div>

                {/* Filter Values */}
                {formData.dataset_name &&
                  (Array.isArray(formData.dataset_name)
                    ? formData.dataset_name.length > 0
                    : true) &&
                  (availableFilters.length > 0 || loadingFilters) && (
                    <div className="mt-2 mb-4">
                      <label
                        className="block text-sm font-semibold mb-3"
                        style={{ color: "var(--text-primary)" }}
                      >
                        Filter Values (optional)
                      </label>
                      {loadingFilters ? (
                        <div className="flex items-center gap-2 py-4 justify-center">
                          <Loader2
                            size={16}
                            className="animate-spin"
                            style={{ color: "var(--text-secondary)" }}
                          />
                          <span
                            className="text-sm"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            Loading filters...
                          </span>
                        </div>
                      ) : (
                        availableFilters.map((group) => (
                          <div
                            key={group.group}
                            className="mb-3 p-3 px-4 rounded-lg border"
                            style={{
                              background: "var(--bg-secondary)",
                              borderColor: "var(--border-color)",
                            }}
                          >
                            <div
                              className="text-[12px] font-semibold uppercase tracking-wide mb-2"
                              style={{ color: "var(--text-secondary)" }}
                            >
                              {group.group.charAt(0).toUpperCase() +
                                group.group.slice(1)}
                            </div>
                            <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                              {group.columns.map((col) => (
                                <div key={col.name}>
                                  <label
                                    className="block text-sm font-semibold mb-2"
                                    style={{ color: "var(--text-primary)" }}
                                  >
                                    {col.display_name}
                                  </label>
                                  <MultiSelect
                                    options={(col.values || []).map((v) => ({
                                      key: v,
                                      text: v,
                                      value: v,
                                    }))}
                                    value={
                                      formData.filter_values[col.name] || []
                                    }
                                    onChange={(v) =>
                                      handleFilterValueChange(col.name, v)
                                    }
                                    placeholder={`All ${col.display_name}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
              </div>

              {/* Section 3: Schedule Configuration */}
              <div>
                <span
                  className="block text-[11px] font-bold uppercase tracking-wider mb-4"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Schedule Configuration
                </span>

                {/* Mode toggle */}
                <div className="mb-4">
                  <div
                    className="inline-flex rounded-lg border overflow-hidden"
                    style={{
                      background: "var(--input-bg)",
                      borderColor: "var(--border-color)",
                    }}
                  >
                    {["simple", "advanced"].map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        className={`px-5 py-[7px] text-[13px] font-medium transition-all ${
                          scheduleMode === mode
                            ? "text-white font-semibold"
                            : "hover:bg-[var(--bg-tertiary)]"
                        }`}
                        style={
                          scheduleMode === mode
                            ? { background: "var(--brand-primary)" }
                            : { color: "var(--text-secondary)" }
                        }
                        onClick={() => setScheduleMode(mode)}
                      >
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {scheduleMode === "simple" ? (
                  <div className="grid grid-cols-3 gap-4 max-sm:grid-cols-1 mb-4">
                    {/* Frequency */}
                    <div>
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: "var(--text-primary)" }}
                      >
                        Frequency
                      </label>
                      <Listbox
                        value={formData.frequency}
                        onChange={(v) => handleFormChange("frequency", v)}
                      >
                        <div className="relative">
                          <ListboxButton
                            className="flex items-center justify-between w-full px-3 py-2.5 rounded-md border text-sm"
                            style={{
                              background: "var(--input-bg)",
                              borderColor: "var(--border-color)",
                              color: "var(--text-primary)",
                            }}
                          >
                            <span className="truncate">
                              {getOptionText(
                                frequencyOptions,
                                formData.frequency
                              )}
                            </span>
                            <ChevronDown
                              size={14}
                              className="shrink-0"
                              style={{ color: "var(--text-secondary)" }}
                            />
                          </ListboxButton>
                          <ListboxOptions
                            anchor="bottom start"
                            className="rounded-lg border shadow-lg p-1 [--anchor-gap:4px] max-h-60 overflow-y-auto z-[60]"
                            style={{
                              background: "var(--card-bg)",
                              borderColor: "var(--border-color)",
                            }}
                          >
                            {frequencyOptions.map((opt) => (
                              <ListboxOption
                                key={opt.key}
                                value={opt.value}
                                className="group flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors data-[focus]:bg-[var(--bg-secondary)]"
                                style={{ color: "var(--text-primary)" }}
                              >
                                <Check
                                  size={14}
                                  className="shrink-0 opacity-0 group-data-[selected]:opacity-100"
                                  style={{ color: "var(--brand-primary)" }}
                                />
                                {opt.text}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </div>
                      </Listbox>
                    </div>

                    {/* Time of Day */}
                    <div>
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: "var(--text-primary)" }}
                      >
                        Time of Day
                      </label>
                      <input
                        type="time"
                        value={formData.time_of_day}
                        onChange={(e) =>
                          handleFormChange("time_of_day", e.target.value)
                        }
                        className="w-full px-3 py-2.5 rounded-md border text-sm outline-none transition-all focus:ring-2 focus:ring-brand-500/20"
                        style={{
                          background: "var(--input-bg)",
                          color: "var(--text-primary)",
                          borderColor: "var(--border-color)",
                        }}
                      />
                    </div>

                    {/* Timezone */}
                    <div>
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: "var(--text-primary)" }}
                      >
                        Timezone
                      </label>
                      <Listbox
                        value={formData.timezone}
                        onChange={(v) => handleFormChange("timezone", v)}
                      >
                        <div className="relative">
                          <ListboxButton
                            className="flex items-center justify-between w-full px-3 py-2.5 rounded-md border text-sm"
                            style={{
                              background: "var(--input-bg)",
                              borderColor: "var(--border-color)",
                              color: "var(--text-primary)",
                            }}
                          >
                            <span className="truncate">
                              {getOptionText(
                                timezoneOptions,
                                formData.timezone
                              )}
                            </span>
                            <ChevronDown
                              size={14}
                              className="shrink-0"
                              style={{ color: "var(--text-secondary)" }}
                            />
                          </ListboxButton>
                          <ListboxOptions
                            anchor="bottom start"
                            className="rounded-lg border shadow-lg p-1 [--anchor-gap:4px] max-h-60 overflow-y-auto z-[60]"
                            style={{
                              background: "var(--card-bg)",
                              borderColor: "var(--border-color)",
                            }}
                          >
                            {timezoneOptions.map((opt) => (
                              <ListboxOption
                                key={opt.key}
                                value={opt.value}
                                className="group flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors data-[focus]:bg-[var(--bg-secondary)]"
                                style={{ color: "var(--text-primary)" }}
                              >
                                <Check
                                  size={14}
                                  className="shrink-0 opacity-0 group-data-[selected]:opacity-100"
                                  style={{ color: "var(--brand-primary)" }}
                                />
                                {opt.text}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </div>
                      </Listbox>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1 mb-4">
                    {/* Cron Expression */}
                    <div>
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: "var(--text-primary)" }}
                      >
                        Cron Expression
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., 0 9 * * 1-5 (weekdays at 9am)"
                        value={formData.cron_expression}
                        onChange={(e) =>
                          handleFormChange("cron_expression", e.target.value)
                        }
                        className="w-full px-3 py-2.5 rounded-md border text-sm outline-none transition-all focus:ring-2 focus:ring-brand-500/20"
                        style={{
                          background: "var(--input-bg)",
                          color: "var(--text-primary)",
                          borderColor: "var(--border-color)",
                        }}
                      />
                    </div>

                    {/* Timezone */}
                    <div>
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: "var(--text-primary)" }}
                      >
                        Timezone
                      </label>
                      <Listbox
                        value={formData.timezone}
                        onChange={(v) => handleFormChange("timezone", v)}
                      >
                        <div className="relative">
                          <ListboxButton
                            className="flex items-center justify-between w-full px-3 py-2.5 rounded-md border text-sm"
                            style={{
                              background: "var(--input-bg)",
                              borderColor: "var(--border-color)",
                              color: "var(--text-primary)",
                            }}
                          >
                            <span className="truncate">
                              {getOptionText(
                                timezoneOptions,
                                formData.timezone
                              )}
                            </span>
                            <ChevronDown
                              size={14}
                              className="shrink-0"
                              style={{ color: "var(--text-secondary)" }}
                            />
                          </ListboxButton>
                          <ListboxOptions
                            anchor="bottom start"
                            className="rounded-lg border shadow-lg p-1 [--anchor-gap:4px] max-h-60 overflow-y-auto z-[60]"
                            style={{
                              background: "var(--card-bg)",
                              borderColor: "var(--border-color)",
                            }}
                          >
                            {timezoneOptions.map((opt) => (
                              <ListboxOption
                                key={opt.key}
                                value={opt.value}
                                className="group flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors data-[focus]:bg-[var(--bg-secondary)]"
                                style={{ color: "var(--text-primary)" }}
                              >
                                <Check
                                  size={14}
                                  className="shrink-0 opacity-0 group-data-[selected]:opacity-100"
                                  style={{ color: "var(--brand-primary)" }}
                                />
                                {opt.text}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </div>
                      </Listbox>
                    </div>
                  </div>
                )}

                {/* Allowed Users */}
                <div>
                  <label
                    className="block text-sm font-semibold mb-2"
                    style={{ color: "var(--text-primary)" }}
                  >
                    Allowed Users (emails, comma-separated)
                  </label>
                  <input
                    type="text"
                    placeholder="user1@example.com, user2@example.com"
                    value={formData.allowed_users}
                    onChange={(e) =>
                      handleFormChange("allowed_users", e.target.value)
                    }
                    className="w-full px-3 py-2.5 rounded-md border text-sm outline-none transition-all focus:ring-2 focus:ring-brand-500/20"
                    style={{
                      background: "var(--input-bg)",
                      color: "var(--text-primary)",
                      borderColor: "var(--border-color)",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div
              className="px-6 py-4 border-t flex justify-end gap-3 shrink-0"
              style={{
                borderColor: "var(--border-color)",
                background: "var(--bg-secondary)",
              }}
            >
              <button
                onClick={closeModal}
                className="px-6 py-2.5 rounded-lg text-sm border transition-colors hover:bg-[var(--bg-tertiary)]"
                style={{
                  borderColor: "var(--border-color)",
                  color: "var(--text-primary)",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={
                  saving ||
                  !formData.analysis_name ||
                  !formData.client ||
                  !formData.dataset_name ||
                  (Array.isArray(formData.dataset_name) &&
                    formData.dataset_name.length === 0) ||
                  formData.frameworks.length === 0 ||
                  (scheduleMode === "advanced" &&
                    !formData.cron_expression.trim())
                }
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 flex items-center gap-2"
                style={{
                  background: saving
                    ? "var(--bg-tertiary)"
                    : "var(--brand-primary)",
                }}
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {modalMode === "create" ? "Create" : "Save Changes"}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* ── Error Modal ── */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            className="w-full max-w-lg rounded-xl shadow-2xl"
            style={{ background: "var(--card-bg)" }}
          >
            <DialogTitle
              className="px-6 py-5 border-b flex items-center gap-2"
              style={{
                borderColor: "var(--border-color)",
                color: "var(--text-primary)",
              }}
            >
              <AlertTriangle size={20} className="text-red-500" />
              <span className="font-semibold text-lg">Analysis Error</span>
            </DialogTitle>
            <div className="p-6">
              <div
                className="p-4 rounded-lg border"
                style={{
                  background: "var(--error-bg)",
                  borderColor: "var(--error-border)",
                }}
              >
                <p className="font-semibold text-sm text-red-600 dark:text-red-400 mb-2">
                  Error Details
                </p>
                <pre
                  className="text-sm whitespace-pre-wrap break-words max-h-[400px] overflow-auto m-0"
                  style={{ color: "var(--text-primary)" }}
                >
                  {selectedError}
                </pre>
              </div>
            </div>
            <div
              className="px-6 py-4 border-t flex justify-end"
              style={{
                borderColor: "var(--border-color)",
                background: "var(--bg-secondary)",
              }}
            >
              <button
                onClick={() => setErrorModalOpen(false)}
                className="px-6 py-2.5 rounded-lg text-sm border transition-colors hover:bg-[var(--bg-tertiary)]"
                style={{
                  borderColor: "var(--border-color)",
                  color: "var(--text-primary)",
                }}
              >
                Close
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <Dialog
        open={confirmOpen}
        onClose={() => dispatch(setConfirmOpen(false))}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            className="w-full max-w-md rounded-xl shadow-2xl"
            style={{ background: "var(--card-bg)" }}
          >
            <DialogTitle
              className="px-6 py-5 border-b font-semibold text-lg"
              style={{
                borderColor: "var(--border-color)",
                color: "var(--text-primary)",
              }}
            >
              Delete Schedule
            </DialogTitle>
            <div
              className="px-6 py-5 text-sm"
              style={{ color: "var(--text-primary)" }}
            >
              Are you sure you want to delete &ldquo;
              {selectedSchedule?.analysis_name}&rdquo;? This action cannot be
              undone.
            </div>
            <div
              className="px-6 py-4 border-t flex justify-end gap-3"
              style={{
                borderColor: "var(--border-color)",
                background: "var(--bg-secondary)",
              }}
            >
              <button
                onClick={() => dispatch(setConfirmOpen(false))}
                className="px-6 py-2.5 rounded-lg text-sm border transition-colors hover:bg-[var(--bg-tertiary)]"
                style={{
                  borderColor: "var(--border-color)",
                  color: "var(--text-primary)",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
};

export default ScheduledAnalysis;
